import { InfoCircleOutlined, LinkOutlined } from '@ant-design/icons'
import { Button, Card, Form, Spin } from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import pushApi from '../../api/push.api'
import AppPushTemplates from '../../components/appPush/AppPushTemplates'
import ContentHeader from '../../components/shared/ContentHeader'
import styles from '../../styles/product.module.scss'
import { salusS3 } from '../../api/s3.api'

const AppPushTemplateDetailPage = () => {
  const { templateId } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [appPushTemplateDetail, setAppPushTemplateDetail] = useState({
    title: '',
    pushTitle: '',
    pushBody: '',
    pushUrl: '',
    pushImageUrl: '',
  })
  const navigate = useNavigate()

  useEffect(() => {
    if (templateId) {
      getAppPushTemplate()
    }
  }, [templateId])

  const getAppPushTemplate = async () => {
    setIsLoading(true)
    try {
      const res = await pushApi.getAppPushTemplateDetail(templateId)
      if (res.status === 200) {
        setAppPushTemplateDetail(res.data)
      }
    } catch (error) {
      console.error('getAppPushTemplate error:', error)
      Swal.fire({
        icon: 'error',
        title: '템플릿 조회 실패',
        text: '템플릿 정보를 불러오는데 실패했습니다.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const s3Upload = async () => {
    try {
      const now = new Date()
      const monthFolder = `${now.getFullYear()}-${String(
        now.getMonth() + 1,
      ).padStart(2, '0')}`

      // 확장자를 png로 고정
      const s3Path = `push/salus/template/${monthFolder}/template_${templateId}.png`
      const params = {
        Bucket: process.env.REACT_APP_S3_BUCKETNAME,
        Key: s3Path,
        Body: appPushTemplateDetail.pushImageUrl.originFileObj,
      }
      const res = await salusS3.putObject(params).promise()

      if (res.$response.httpResponse.statusCode === 200) {
        return s3Path
      }
      return null
    } catch (error) {
      console.error('s3Upload error:', error)
      return null
    }
  }

  // 수정 파라미터 생성
  const createUpdatePushParams = async () => {
    let imageUrl = null
    if (appPushTemplateDetail.pushImageUrl) {
      if (typeof appPushTemplateDetail.pushImageUrl === 'string') {
        // 기존 URL인 경우 그대로 사용
        imageUrl = appPushTemplateDetail.pushImageUrl
      } else {
        // 새로운 파일인 경우 업로드
        const s3Path = await s3Upload()
        if (!s3Path) {
          throw new Error('이미지 업로드에 실패했습니다.')
        }
        imageUrl = `${process.env.REACT_APP_S3_HOST}/${s3Path}`
      }
    }

    const params = {
      title: appPushTemplateDetail.title,
      pushTitle: appPushTemplateDetail.pushTitle,
      pushBody: appPushTemplateDetail.pushBody,
      pushUrl: appPushTemplateDetail.pushUrl || null,
      pushImageUrl: imageUrl,
    }

    if (!validatePushParams(params)) {
      return null
    }

    return params
  }
  const validatePushParams = (params) => {
    const errors = []

    if (!params.title?.trim()) {
      errors.push('템플릿 이름을 입력해주세요.')
    }
    if (!params.pushTitle?.trim()) {
      errors.push('푸시 제목을 입력해주세요.')
    }
    if (!params.pushBody?.trim()) {
      errors.push('푸시 내용을 입력해주세요.')
    }

    if (errors.length > 0) {
      alert(errors.join('\n'))
      return false
    } else {
      return true
    }
  }

  const handleUpdateTemplate = async () => {
    try {
      const result = await Swal.fire({
        title: '푸시 템플릿 수정',
        html: `템플릿 제목: <b>${appPushTemplateDetail.title}</b><br>으로 수정하시겠습니까?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: '수정',
        cancelButtonText: '취소',
        reverseButtons: true,
      })

      if (result.isConfirmed) {
        setIsLoading(true)
        Swal.fire({
          title: '수정 중...',
          html: '템플릿을 수정하고 있습니다.',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading()
          },
        })

        const params = await createUpdatePushParams()
        const res = await pushApi.updateAppPushTemplate(templateId, params)

        if (res.status === 200) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: '템플릿이 성공적으로 수정되었습니다.',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          })
          setIsEdit(false)
          getAppPushTemplate()
        }
      }
    } catch (error) {
      console.error('updateTemplate error:', error)
      Swal.fire({
        icon: 'error',
        title: '템플릿 수정 실패',
        text: error.message || '템플릿 수정 중 오류가 발생했습니다.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteTemplate = async () => {
    try {
      const result = await Swal.fire({
        title: '템플릿 삭제',
        text: '삭제하시겠습니까?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '삭제',
        cancelButtonText: '취소',
        reverseButtons: true,
      })

      if (result.isConfirmed) {
        setIsLoading(true)
        const res = await pushApi.deleteAppPushTemplate(templateId)
        if (res.status === 200) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: '템플릿이 성공적으로 삭제되었습니다.',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          })
          navigate('/appPush/templates')
        }
      }
    } catch (error) {
      console.error('deleteTemplate error:', error)
      Swal.fire({
        icon: 'error',
        title: '템플릿 삭제 실패',
        text: '템플릿 삭제 중 오류가 발생했습니다.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setAppPushTemplateDetail((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleImageChange = (info) => {
    if (info.fileList.length > 0) {
      setAppPushTemplateDetail((prev) => ({
        ...prev,
        pushImageUrl: info.fileList[0],
      }))
    } else {
      setAppPushTemplateDetail((prev) => ({
        ...prev,
        pushImageUrl: null,
      }))
    }
  }

  return (
    <Spin spinning={isLoading}>
      <div style={{ width: '100%', paddingBottom: '8rem' }}>
        {isEdit ? (
          <AppPushTemplates
            headerTitle="푸시 템플릿 수정"
            pushData={appPushTemplateDetail}
            handleChange={handleChange}
            handleImageChange={handleImageChange}
          />
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                background: '#fff',
              }}
            >
              <ContentHeader title="푸시 템플릿 상세" />
            </div>

            <Card
              title={
                <>
                  <InfoCircleOutlined style={{ marginRight: 8 }} />
                  템플릿 이름
                </>
              }
              style={{ marginBottom: 20 }}
            >
              <Form.Item
                label="템플릿 이름"
                style={{ marginBottom: 16 }}
                labelCol={{ flex: '0 0 100px' }}
                wrapperCol={{ flex: '1 1 auto' }}
                labelAlign="left"
              >
                <div style={{ width: '50%' }}>
                  {appPushTemplateDetail.title}
                </div>
              </Form.Item>
            </Card>

            <Card
              title={
                <>
                  <InfoCircleOutlined style={{ marginRight: 8 }} />
                  푸시 내용
                </>
              }
              style={{ marginBottom: 20 }}
            >
              <Form.Item
                label="제목"
                style={{ marginBottom: 16 }}
                labelCol={{ flex: '0 0 100px' }}
                wrapperCol={{ flex: '1 1 auto' }}
                labelAlign="left"
              >
                <div style={{ width: '50%' }}>
                  {appPushTemplateDetail.pushTitle}
                </div>
              </Form.Item>

              <Form.Item
                label="내용"
                style={{ marginBottom: 16 }}
                labelCol={{ flex: '0 0 100px' }}
                wrapperCol={{ flex: '1 1 auto' }}
                labelAlign="left"
              >
                <div style={{ width: '50%' }}>
                  {appPushTemplateDetail.pushBody}
                </div>
              </Form.Item>

              <Form.Item
                label="URL"
                style={{ marginBottom: 16 }}
                labelCol={{ flex: '0 0 100px' }}
                wrapperCol={{ flex: '1 1 auto' }}
                labelAlign="left"
              >
                <div
                  style={{
                    width: '50%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <LinkOutlined style={{ marginRight: 8 }} />
                  {appPushTemplateDetail.pushUrl || '-'}
                </div>
              </Form.Item>

              <Form.Item
                label="이미지"
                style={{ marginBottom: 0 }}
                labelCol={{ flex: '0 0 100px' }}
                wrapperCol={{ flex: '1 1 auto' }}
                labelAlign="left"
              >
                <div style={{ width: '25vw', minWidth: 200 }}>
                  {appPushTemplateDetail.pushImageUrl ? (
                    <img
                      src={
                        typeof appPushTemplateDetail.pushImageUrl === 'string'
                          ? appPushTemplateDetail.pushImageUrl
                          : URL.createObjectURL(
                              appPushTemplateDetail.pushImageUrl.originFileObj,
                            )
                      }
                      alt="푸시 이미지"
                      style={{ maxWidth: '200px', maxHeight: '200px' }}
                    />
                  ) : (
                    '-'
                  )}
                </div>
              </Form.Item>
            </Card>
          </>
        )}

        <div className={`${styles['product-register-fixed-footer']}`}>
          {isEdit ? (
            <>
              <Button
                onClick={() => setIsEdit(false)}
                style={{ marginRight: 8 }}
              >
                취소
              </Button>
              <Button type="primary" onClick={handleUpdateTemplate}>
                저장
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleDeleteTemplate}
                danger
                style={{ marginRight: 8 }}
              >
                삭제
              </Button>
              <Button type="primary" onClick={() => setIsEdit(true)}>
                수정
              </Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  )
}

export default AppPushTemplateDetailPage
